import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Settings, LogOut, User, ChevronUp, Shield } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { resolveIsAdmin } from '../../utils/adminAccess';

const HeadersAuth = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { logout, user, userProfile } = useAuthContext();
  const [operatorClaim, setOperatorClaim] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setOperatorClaim(false);
      return undefined;
    }
    user
      .getIdTokenResult()
      .then((r) => {
        if (!cancelled) setOperatorClaim(r.claims?.qfaaOperator === true);
      })
      .catch(() => {
        if (!cancelled) setOperatorClaim(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const isAdmin = resolveIsAdmin({ user, userProfile, operatorClaim });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Failsafe: force redirect to login even if React re-render doesn't trigger
    window.location.href = '/login';
  };

  if (isLoggingOut) {
    return (
      <div className="fixed inset-0 z-[100] bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Logging out...</p>
        </div>
      </div>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0b0d2a] flex justify-between items-center px-6 lg:px-10 py-4">
      <div className="flex items-center">
        <Link to="/" className="flex items-center space-x-2">
          <img
            src="/logojt.png"
            alt="JTNC Logo"
            className="h-[60px] w-[260px] object-cover"
          />
        </Link>
      </div>

      <nav className="flex items-center space-x-6 text-lg">
        <button 
          onClick={() => {
            // Set session storage to bypass broker connection check
            sessionStorage.setItem('allowDashboardNoBroker', '1');
            window.location.href = '/dashboard';
          }}
          className="cursor-pointer"
        >
          <img
            src="/qfaa-logo.png"
            alt="JTNCBOT Logo"
            className="h-16 w-auto object-contain drop-shadow-md"
          />
        </button>
        <Link to="/bots" className="text-gray-300 hover:text-cyan-300">
          Algos
        </Link>
        <Link to="/upgrade" className="text-gray-300 hover:text-cyan-300">
          Upgrade
        </Link>
        <Link to="/learn" className="text-gray-300 hover:text-cyan-300">
          Training Videos
        </Link>
        
        {/* Account Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            data-testid="account-menu-toggle"
            className="bg-cyan-400 text-[#0b0d2a] px-4 py-2 rounded-full font-semibold hover:bg-cyan-300 flex items-center space-x-2 transition-all duration-200"
          >
            <User className="w-5 h-5" />
            <span>Account</span>
            <ChevronUp className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-0' : 'rotate-180'}`} />
          </button>
          
          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-2 z-50">
              <Link
                to="/account"
                className="flex items-center space-x-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
                onClick={() => {
                  setIsDropdownOpen(false);
                  // Set session storage to bypass broker connection check for account settings
                  sessionStorage.setItem('allowAccountNoBroker', '1');
                }}
              >
                <Settings className="w-5 h-5" />
                <span>Account Settings</span>
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center space-x-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <Shield className="w-5 h-5" />
                  <span>Admin</span>
                </Link>
              )}
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center space-x-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150 w-full text-left"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default HeadersAuth;
